import React, { useEffect, useMemo, useState } from 'react';
import { cancelAppointment, getAppointmentsForRange, type CalendarAppointment } from '../services/firebase';
import type { OrganizationKey } from '../types/organizations';
import { formatMdy, formatMdyTime } from '../types/prospecting';

type AppUser = { id: string; email: string; name: string; role: 'admin' | 'user' | 'SuperAdmin'; createdAt: string };

type DayAppointment = CalendarAppointment & {
  startMs: number;
  endMs: number;
  hasConflict: boolean;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfWeek = (d: Date): Date => {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday-start week
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const addDays = (d: Date, days: number): Date => new Date(d.getTime() + days * DAY_MS);

const dateKeyLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function AppointmentCalendarView(props: { currentUser: AppUser; orgKey: OrganizationKey }) {
  const { currentUser, orgKey } = props;
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState<CalendarAppointment[]>([]);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadAppointments = React.useCallback(async (mountedRef?: { current: boolean }) => {
    setLoading(true);
    try {
      const start = new Date(weekStart);
      const end = addDays(weekStart, 4);
      end.setHours(23, 59, 59, 999);
      const rows = await getAppointmentsForRange(currentUser.id, start.toISOString(), end.toISOString(), orgKey);
      if (mountedRef && !mountedRef.current) return;
      setAppointments(rows);
    } finally {
      if (!mountedRef || mountedRef.current) setLoading(false);
    }
  }, [currentUser.id, weekStart, orgKey]);

  useEffect(() => {
    const mountedRef = { current: true };
    void loadAppointments(mountedRef);
    return () => {
      mountedRef.current = false;
    };
  }, [loadAppointments]);

  const handleCancelAppointment = async (appointment: CalendarAppointment) => {
    const ok = window.confirm(`Cancel "${appointment.name}" on ${formatMdyTime(new Date(appointment.appointmentAt))}?`);
    if (!ok) return;
    setCancellingId(appointment.id);
    try {
      await cancelAppointment(currentUser.id, appointment);
      await loadAppointments();
    } catch (error: any) {
      window.alert(error?.message || 'Failed to cancel appointment.');
    } finally {
      setCancellingId(null);
    }
  };

  const weekDays = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const appointmentsByDay = useMemo(() => {
    const grouped: Record<string, DayAppointment[]> = {};
    weekDays.forEach((d) => {
      grouped[dateKeyLocal(d)] = [];
    });

    appointments.forEach((a) => {
      if (!a.appointmentAt) return;
      const start = new Date(a.appointmentAt);
      if (Number.isNaN(start.getTime())) return;
      const duration = Number(a.durationMinutes) || 30;
      const dayKey = dateKeyLocal(start);
      if (!grouped[dayKey]) grouped[dayKey] = [];
      grouped[dayKey].push({
        ...a,
        startMs: start.getTime(),
        endMs: start.getTime() + duration * 60 * 1000,
        hasConflict: false
      });
    });

    Object.keys(grouped).forEach((dayKey) => {
      const dayRows = grouped[dayKey].sort((a, b) => a.startMs - b.startMs);
      for (let i = 0; i < dayRows.length; i++) {
        for (let j = i + 1; j < dayRows.length; j++) {
          const a = dayRows[i];
          const b = dayRows[j];
          if (b.startMs >= a.endMs) break;
          if (a.startMs < b.endMs && b.startMs < a.endMs) {
            dayRows[i] = { ...a, hasConflict: true };
            dayRows[j] = { ...b, hasConflict: true };
          }
        }
      }
      grouped[dayKey] = dayRows;
    });

    return grouped;
  }, [appointments, weekDays]);

  const weekLabel = `${formatMdy(weekDays[0])} - ${formatMdy(weekDays[4])}`;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h3 style={{ margin: 0 }}>Calendar (Week)</h3>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 12 }}>
            Visual schedule to avoid double-booking.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={styles.controlBtn} onClick={() => setWeekStart((prev) => addDays(prev, -7))}>
            Previous week
          </button>
          <button style={styles.controlBtn} onClick={() => setWeekStart(startOfWeek(new Date()))}>
            This week
          </button>
          <button style={styles.controlBtn} onClick={() => setWeekStart((prev) => addDays(prev, 7))}>
            Next week
          </button>
          <span style={{ fontSize: 12, color: '#374151' }}>{weekLabel}</span>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#6b7280', padding: 12 }}>Loading appointments...</div>
      ) : (
        <div style={styles.weekGrid}>
          {weekDays.map((d) => {
            const key = dateKeyLocal(d);
            const dayRows = appointmentsByDay[key] || [];
            return (
              <section key={key} style={styles.dayColumn}>
                <div style={styles.dayHeader}>
                  <strong>{d.toLocaleDateString('en-US', { weekday: 'short' })}</strong>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{formatMdy(d)}</span>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {dayRows.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>No appointments</div>
                  ) : (
                    dayRows.map((a) => {
                      const dt = new Date(a.appointmentAt);
                      return (
                        <div key={a.id} style={{ ...styles.appointmentCard, borderColor: a.hasConflict ? '#fca5a5' : '#d1fae5' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <strong style={{ fontSize: 13 }}>{a.name}</strong>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              {a.hasConflict && <span style={styles.conflictBadge}>Overlap</span>}
                              <button
                                style={{
                                  ...styles.cancelBtn,
                                  opacity: cancellingId === a.id ? 0.6 : 1,
                                  cursor: cancellingId === a.id ? 'wait' : 'pointer'
                                }}
                                onClick={() => void handleCancelAppointment(a)}
                                disabled={cancellingId === a.id}
                              >
                                {cancellingId === a.id ? 'Cancelling...' : 'Cancel'}
                              </button>
                            </div>
                          </div>
                          <div style={styles.appointmentLine}>{formatMdyTime(dt)}</div>
                          <div style={styles.appointmentLine}>
                            {a.appointmentType ? a.appointmentType.replace('_', ' ') : 'appointment'} • {a.durationMinutes || 30} min
                          </div>
                          {a.appointmentContactName ? <div style={styles.appointmentLine}>Contact: {a.appointmentContactName}</div> : null}
                          {a.appointmentContactPhone ? <div style={styles.appointmentLine}>Phone: {a.appointmentContactPhone}</div> : null}
                          {a.appointmentContactEmail ? <div style={styles.appointmentLine}>Email: {a.appointmentContactEmail}</div> : null}
                          {a.appointmentLocation ? <div style={styles.appointmentLine}>Location: {a.appointmentLocation}</div> : null}
                          {a.note ? <div style={styles.appointmentNote}>Note: {a.note}</div> : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 16 },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 12
  },
  controlBtn: {
    padding: '8px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 12
  },
  weekGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 10
  },
  dayColumn: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 10,
    minHeight: 140
  },
  dayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    borderBottom: '1px solid #f3f4f6',
    paddingBottom: 6
  },
  appointmentCard: {
    border: '1px solid #d1fae5',
    borderRadius: 8,
    background: '#f9fafb',
    padding: 8
  },
  appointmentLine: {
    fontSize: 12,
    color: '#374151',
    marginTop: 4
  },
  appointmentNote: {
    marginTop: 6,
    fontSize: 12,
    color: '#111827'
  },
  conflictBadge: {
    fontSize: 11,
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 999,
    padding: '1px 6px'
  },
  cancelBtn: {
    fontSize: 11,
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 999,
    padding: '2px 8px',
    background: '#fff',
    whiteSpace: 'nowrap'
  }
};
